import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Grid, Avatar, Chip } from '@material-ui/core';
import clsx from 'clsx';

import { openModal, setFooter } from 'features/utilsSlice';
import { formatPrice, getGigOwner, getGigStatus } from 'utils/helpers';
import useCurrentProfile from 'hooks/useCurrentProfile';
import useIsInfluencer from 'hooks/useIsInfluencer';
import NoAvatarImage from 'public/images/no-avatar.svg';
import JobDetail from './JobDetail';
import AdditionalDetails from './AdditionalDetails';
import GigDetails from './GigDetails';
import InfluencerInventory from './InfluencerInventory';
import MilestoneList from 'components/milestones/MilestoneList';
import ProfileRatingStar from 'components/advertiserProfile/ProfileRatingStar';
import AdvertiserProfileDialog from 'components/advertiserProfile/AdvertiserProfileDialog';
import InfluencerGigFooter from 'components/gigs/InfluencerGigFooter';
import JobDescription from 'components/gigs/JobDescription';
import CustomBadge from 'components/CustomBadge';
import BottomButton from 'components/BottomButton';
import styles from 'styles/components/gigs/NewPublicGig.module.scss';

const noPadding = { paddingBottom: '0 !important' };
const noMargin = { margin: 0 };
const avatarFullSize = { width: '100%', height: '100%' };
const avatarStyle = { width: 40, height: 40, border: 'solid 1px #e8e8e8' };

const NewPublicGigDesktop = ({ gig, isDialog, handleClose, isPreview }) => {
  const [padding, setPadding] = useState({});
  const isInfluencer = useIsInfluencer();
  const isLoggedIn = useSelector(state => state.auth.loggedIn);
  const currentUser = useSelector(state => state.auth.currentUser);
  const dispatch = useDispatch();
  const router = useRouter();
  const profile = useCurrentProfile();
  const gigOwner = getGigOwner(currentUser, gig.user_id);
  const gigOFConnected = gig.only_fans_connection_id;
  const gigOFEnabled = gigOFConnected && gig.only_fans_connection?.enabled;

  useEffect(() => {
    dispatch(setFooter(false));

    return () => dispatch(setFooter(true));
  }, [dispatch, handleClose, isPreview]);

  return (
    <div
      className={clsx(styles.publicGigContainer, {
        [styles.moveUp]: isDialog || isPreview,
      })}
      style={!isLoggedIn || gig.accepted_at ? padding : noPadding}
    >
      <div className={styles.background}>
        <div className={styles.title}>
          <CustomBadge
            title={getGigStatus(gig.status)}
            active={['active', 'pending'].includes(gig.status)}
            styles={{ position: 'absolute', top: 15 }}
          />

          <h2>{gig.title}</h2>
          {gigOFConnected && (
            <Chip
              label={gigOFEnabled ? 'Automatic GG' : 'Automatic GG (Disabled)'}
              className={clsx({
                [styles.OFConnectedChip]: gigOFEnabled,
                [styles.OFDisabledChip]: !gigOFEnabled,
              })}
            />
          )}
        </div>
      </div>

      <div className={styles.purpleBox}>
        <div className={styles.price}>
          {gig.special_offer_ad ? (
            <h2>Special Offer</h2>
          ) : (
            <>
              <span style={{ opacity: 0.6 }}> {gigOwner ? 'Budget' : "Buyer's budget"}:</span>{' '}
              <h2>{formatPrice(gigOwner ? gig.budget : gig.amount)}</h2>
            </>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Avatar
            onClick={() => {
              if (isPreview) return;

              if (!gig.user.username) return;

              dispatch(
                openModal({
                  name: 'Advertiser Profile',
                  data: {
                    username: gig.user.username,
                  },
                })
              );
            }}
            src={gig.user.avatar_url}
            alt="Profile"
            style={avatarStyle}
            className={clsx(styles.avatar, { [styles.noHover]: isPreview })}
          >
            <NoAvatarImage style={avatarFullSize} />
          </Avatar>

          <div className={styles.advertiserInfo}>
            <span style={{ fontFamily: 'Avenir-Heavy' }}>{gig.user.display_name}</span>

            <ProfileRatingStar rating={gig.user.average_rating} />
          </div>
        </div>
      </div>

      <Grid container className={styles.desktopContainer}>
        <Grid item md={7} style={noMargin}>
          <JobDescription gig={gig} hasDate />
          <MilestoneList
            gig={gig}
            newStyles={{ backgroundColor: '#f8f9fb' }}
            isPreview={isPreview}
          />
        </Grid>

        <Grid item md={4}>
          <h3 style={{ padding: '10px 15px 15px' }}>Details</h3>

          <JobDetail gig={gig} hasPayment={false} />

          <InfluencerInventory gig={gig} />

          <AdditionalDetails gig={gig} />

          <GigDetails gig={gig} />
        </Grid>
      </Grid>

      {((isInfluencer && profile && !gigOwner) || !isLoggedIn) && gig.status === 'pending' && (
        <InfluencerGigFooter
          handleClose={handleClose}
          gig={gig}
          setPadding={setPadding}
          fullWidth
        />
      )}

      {gigOwner && gig.status === 'pending' && !isPreview && (
        <BottomButton
          handleClick={() => {
            router.push(`/gigs/${gig.id}`);
            handleClose();
          }}
          buttonText="Hire for this gig"
        />
      )}

      {!isPreview && <AdvertiserProfileDialog />}
    </div>
  );
};

NewPublicGigDesktop.propTypes = {
  gig: PropTypes.object.isRequired,
  handleClose: PropTypes.func,
  isDialog: PropTypes.bool,
  isPreview: PropTypes.bool,
};

NewPublicGigDesktop.defaultProps = {
  isDialog: false,
  handleClose: () => {},
  isPreview: false,
};

export default NewPublicGigDesktop;
